import { c as _c } from "react/compiler-runtime";
import { Fragment, type ReactElement, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { removeBaseEndpoint } from "@/helpers/Helpers";
import type { GithubPreviewEnvelope, GithubPreviewResponse } from "@/services/api/github-preview-api";
import ChatItemHrefButtons from "./ChatItemHrefButtons";
import GithubPreviewStatusBadge from "./GithubPreviewStatusBadge";
import { useLinkPreviewVariant, type LinkPreviewVariant } from "./LinkPreviewContext";
export interface OpenGraphPreviewData {
  title?: unknown | undefined;
  description?: unknown | undefined;
  url?: unknown | undefined;
  siteName?: unknown | undefined;
  site_name?: unknown | undefined;
  domain?: unknown | undefined;
  source?: unknown | undefined;
  canonicalUrl?: unknown | undefined;
  canonical_url?: unknown | undefined;
  image?: unknown | undefined;
  imageUrl?: unknown | undefined;
  image_url?: unknown | undefined;
  images?: unknown | undefined;
  ogImage?: unknown | undefined;
  og_image?: unknown | undefined;
  thumbnailUrl?: unknown | undefined;
  thumbnail_url?: unknown | undefined;
  [key: string]: unknown;
}
interface OpenGraphPreviewProps {
  readonly href: string;
  readonly preview?: OpenGraphPreviewData | null | undefined;
  readonly variant?: LinkPreviewVariant | undefined;
  readonly imageOnly?: boolean | undefined;
  readonly hideActions?: boolean | undefined;
}
type MaybeRecord = Record<string, unknown>;
const TITLE_KEYS = ["title", "ogTitle", "name"];
const DESCRIPTION_KEYS = ["description", "ogDescription", "summary"];
const DOMAIN_KEYS = ["domain", "siteName", "site_name", "source"];
const URL_KEYS = ["url", "canonicalUrl", "canonical_url"];
const IMAGE_KEYS = ["image", "imageUrl", "image_url", "ogImage", "og_image", "thumbnailUrl", "thumbnail_url", "cover", "coverImage", "secureUrl"];
const IMAGE_COLLECTION_KEYS = ["images", "ogImages", "og_images", "thumbnails"];
const LONG_UNBROKEN_SEGMENT_THRESHOLD = 32;
function readFirstString(data: OpenGraphPreviewData | null | undefined, keys: readonly string[]): string | undefined {
  if (!data) {
    return undefined;
  }
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return undefined;
}
function extractImageFromValue(value: unknown): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      const extracted = extractImageFromValue(entry);
      if (extracted) {
        return extracted;
      }
    }
    return undefined;
  }
  if (typeof value === "object") {
    const record = value as MaybeRecord;
    return extractImageFromValue(record["url"]) ?? extractImageFromValue(record["secure_url"]) ?? extractImageFromValue(record["secureUrl"]) ?? extractImageFromValue(record["src"]) ?? extractImageFromValue(record["href"]) ?? extractImageFromValue(record["image"]) ?? extractImageFromValue(record["thumbnail"]) ?? extractImageFromValue(record["contentUrl"]) ?? extractImageFromValue(record["content_url"]);
  }
  return undefined;
}
function extractImageUrl(data: OpenGraphPreviewData | null | undefined): string | undefined {
  if (!data) {
    return undefined;
  }
  for (const key of IMAGE_KEYS) {
    const imageUrl = extractImageFromValue(data[key]);
    if (imageUrl) {
      return imageUrl;
    }
  }
  for (const key of IMAGE_COLLECTION_KEYS) {
    const imageUrl = extractImageFromValue(data[key]);
    if (imageUrl) {
      return imageUrl;
    }
  }
  return undefined;
}
function wrapLongUnbrokenSegments(value: string | undefined): ReactNode {
  if (!value) {
    return value ?? "";
  }
  const tokens = value.split(/(\s+)/);
  let mutated = false;
  let offset = 0;
  const nodes = tokens.map(token => {
    if (!token) {
      return null;
    }
    const key = `og-wrap-${offset}`;
    offset += token.length;
    const trimmed = token.trim();
    if (trimmed.length >= LONG_UNBROKEN_SEGMENT_THRESHOLD) {
      mutated = true;
      return <span key={key} className="tw-break-all">
            {token}
          </span>;
    }
    return <Fragment key={key}>{token}</Fragment>;
  }).filter((token): token is ReactElement => token !== null);
  if (!mutated) {
    return value;
  }
  return nodes;
}
function extractDomainFromUrl(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return undefined;
  }
  const candidates = [trimmed];
  if (!/^https?:\/\//i.test(trimmed)) {
    candidates.push(`https://${trimmed.replace(/^\/+/, "")}`);
    candidates.push(`http://${trimmed.replace(/^\/+/, "")}`);
  }
  for (const candidate of candidates) {
    try {
      const parsed = new URL(candidate);
      const hostname = parsed.hostname.replace(/^www\./i, "");
      if (hostname) {
        return hostname;
      }
    } catch {
      // ignore and continue
    }
  }
  return undefined;
}
function deriveDomain(href: string, preview: OpenGraphPreviewData | null | undefined): string | undefined {
  return readFirstString(preview, DOMAIN_KEYS) ?? extractDomainFromUrl(readFirstString(preview, URL_KEYS)) ?? extractDomainFromUrl(href);
}
function isGithubPreviewResponse(value: unknown): value is GithubPreviewResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const type = (value as {
    readonly type?: unknown;
  }).type;
  return type === "github.issue" || type === "github.pull_request";
}
function extractGithubPreview(preview: OpenGraphPreviewData | null | undefined): GithubPreviewResponse | null {
  const githubPreview = (preview as GithubPreviewEnvelope | null | undefined)?.githubPreview;
  return isGithubPreviewResponse(githubPreview) ? githubPreview : null;
}
function getRelativeHref(href: string): string | undefined {
  const relative = removeBaseEndpoint(href);
  if (typeof relative !== "string" || relative.length === 0) {
    return undefined;
  }
  return relative.startsWith("/") ? relative : undefined;
}
export function LinkPreviewCardLayout(t0) {
  const $ = _c(13);
  const {
    href,
    children,
    variant,
    hideActions: t1
  } = t0;
  const hideActions = t1 === undefined ? false : t1;
  const contextVariant = useLinkPreviewVariant();
  const resolvedVariant = variant ?? contextVariant;
  let t2;
  if ($[0] !== href) {
    t2 = getRelativeHref(href);
    $[0] = href;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const relativeHref = t2;
  if (resolvedVariant === "home") {
    let t3;
    if ($[2] !== children) {
      t3 = <div className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-max-w-full tw-items-stretch"><div className="tw-h-full tw-min-w-0 tw-max-w-full tw-flex-1 tw-overflow-hidden focus-within:tw-overflow-visible">{children}</div></div>;
      $[2] = children;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  let t3;
  if ($[4] !== children) {
    t3 = <div className="tw-h-full tw-min-h-0 tw-min-w-0 tw-max-w-full tw-overflow-hidden focus-within:tw-overflow-visible">{children}</div>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== hideActions || $[7] !== href || $[8] !== relativeHref) {
    t4 = !hideActions && <ChatItemHrefButtons href={href} relativeHref={relativeHref} layout="overlay" />;
    $[6] = hideActions;
    $[7] = href;
    $[8] = relativeHref;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="tw-group/link-card tw-relative tw-h-full tw-w-full tw-min-w-0 tw-max-w-full">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
export function hasOpenGraphContent(preview: OpenGraphPreviewData | null | undefined): boolean {
  if (!preview) {
    return false;
  }
  return Boolean(readFirstString(preview, TITLE_KEYS) ?? readFirstString(preview, DESCRIPTION_KEYS) ?? extractImageUrl(preview));
}
export default function OpenGraphPreview(t0) {
  const $ = _c(75);
  const {
    href,
    preview,
    variant,
    imageOnly: t1,
    hideActions: t2
  } = t0;
  const imageOnly = t1 === undefined ? false : t1;
  const hideActions = t2 === undefined ? false : t2;
  const contextVariant = useLinkPreviewVariant();
  const resolvedVariant = variant ?? contextVariant;
  let t3;
  if ($[0] !== href) {
    t3 = getRelativeHref(href);
    $[0] = href;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const relativeHref = t3;
  const effectiveHref = relativeHref ?? href;
  const isExternalLink = !relativeHref;
  const linkTarget = isExternalLink ? "_blank" : undefined;
  const linkRel = isExternalLink ? "noopener noreferrer" : undefined;
  if (typeof preview === "undefined") {
    if (resolvedVariant === "home") {
      let t4;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="tw-absolute tw-inset-0 tw-animate-pulse tw-bg-gradient-to-br tw-from-iron-900/40 tw-via-iron-800/20 tw-to-iron-900/40" />;
        $[2] = t4;
      } else {
        t4 = $[2];
      }
      let t5;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <div className="tw-relative tw-h-full tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-black/30" data-testid="og-preview-skeleton">{t4}<div className="tw-relative tw-z-10 tw-flex tw-h-full tw-flex-col tw-justify-end tw-gap-2 tw-p-4"><div className="tw-h-3 tw-w-24 tw-rounded tw-bg-iron-800/50" /><div className="tw-h-5 tw-w-3/4 tw-rounded tw-bg-iron-800/40" /><div className="tw-h-4 tw-w-2/3 tw-rounded tw-bg-iron-800/30" /></div></div>;
        $[3] = t5;
      } else {
        t5 = $[3];
      }
      let t6;
      if ($[4] !== hideActions || $[5] !== href || $[6] !== resolvedVariant) {
        t6 = <LinkPreviewCardLayout href={href} variant={resolvedVariant} hideActions={hideActions}>{t5}</LinkPreviewCardLayout>;
        $[4] = hideActions;
        $[5] = href;
        $[6] = resolvedVariant;
        $[7] = t6;
      } else {
        t6 = $[7];
      }
      return t6;
    }
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-h-full tw-min-h-0 tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4"><div className="tw-flex tw-h-full tw-min-h-0 tw-animate-pulse tw-flex-col tw-justify-end tw-gap-y-3" data-testid="og-preview-skeleton"><div className="tw-h-16 tw-w-full tw-flex-shrink-0 tw-rounded-lg tw-bg-iron-800/60" /><div className="tw-h-4 tw-w-3/4 tw-rounded tw-bg-iron-800/40" /><div className="tw-h-3 tw-w-full tw-rounded tw-bg-iron-800/30" /><div className="tw-h-3 tw-w-2/3 tw-rounded tw-bg-iron-800/20" /></div></div>;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== hideActions || $[10] !== href || $[11] !== resolvedVariant) {
      t5 = <LinkPreviewCardLayout href={href} variant={resolvedVariant} hideActions={hideActions}>{t4}</LinkPreviewCardLayout>;
      $[9] = hideActions;
      $[10] = href;
      $[11] = resolvedVariant;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  let t4;
  if ($[13] !== preview) {
    t4 = readFirstString(preview, TITLE_KEYS);
    $[13] = preview;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const title = t4;
  let t5;
  let t6;
  if ($[15] !== effectiveHref || $[16] !== hideActions || $[17] !== href || $[18] !== imageOnly || $[19] !== linkRel || $[20] !== linkTarget || $[21] !== preview || $[22] !== resolvedVariant || $[23] !== title) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const description = readFirstString(preview, DESCRIPTION_KEYS);
      let t7;
      if ($[26] !== preview) {
        t7 = extractImageUrl(preview);
        $[26] = preview;
        $[27] = t7;
      } else {
        t7 = $[27];
      }
      const imageUrl = t7;
      let t8;
      if ($[28] !== href || $[29] !== preview) {
        t8 = deriveDomain(href, preview);
        $[28] = href;
        $[29] = preview;
        $[30] = t8;
      } else {
        t8 = $[30];
      }
      const domain = t8;
      let t9;
      if ($[31] !== preview) {
        t9 = extractGithubPreview(preview);
        $[31] = preview;
        $[32] = t9;
      } else {
        t9 = $[32];
      }
      const githubPreview = t9;
      const hasContent = Boolean(title ?? description ?? imageUrl);
      if (imageOnly && imageUrl) {
        const t10 = title ?? domain ?? "Link preview";
        let t11;
        if ($[33] !== imageUrl || $[34] !== t10) {
          t11 = <div className="tw-relative tw-aspect-[16/9] tw-w-full tw-overflow-hidden tw-bg-iron-900/60"><Image src={imageUrl} alt={t10} fill={true} className="tw-object-cover" loading="lazy" sizes="(max-width: 768px) 100vw, 480px" unoptimized={true} /></div>;
          $[33] = imageUrl;
          $[34] = t10;
          $[35] = t11;
        } else {
          t11 = $[35];
        }
        let t12;
        if ($[36] !== effectiveHref || $[37] !== linkRel || $[38] !== linkTarget || $[39] !== t11) {
          t12 = <Link href={effectiveHref} target={linkTarget} rel={linkRel} onClick={_temp} className="tw-block tw-h-full tw-min-h-0 tw-w-full tw-overflow-hidden tw-rounded-xl tw-no-underline" data-testid="og-preview-card">{t11}</Link>;
          $[36] = effectiveHref;
          $[37] = linkRel;
          $[38] = linkTarget;
          $[39] = t11;
          $[40] = t12;
        } else {
          t12 = $[40];
        }
        let t13;
        if ($[41] !== hideActions || $[42] !== href || $[43] !== resolvedVariant || $[44] !== t12) {
          t13 = <LinkPreviewCardLayout href={href} variant={resolvedVariant} hideActions={hideActions}>{t12}</LinkPreviewCardLayout>;
          $[41] = hideActions;
          $[42] = href;
          $[43] = resolvedVariant;
          $[44] = t12;
          $[45] = t13;
        } else {
          t13 = $[45];
        }
        t6 = t13;
        break bb0;
      }
      if (!hasContent) {
        if (resolvedVariant === "home") {
          let t10;
          if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
            t10 = <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-iron-900/30 tw-via-black/20 tw-to-iron-900/30" />;
            $[46] = t10;
          } else {
            t10 = $[46];
          }
          let t11;
          if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
            t11 = <span className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-iron-400">Link unavailable</span>;
            $[47] = t11;
          } else {
            t11 = $[47];
          }
          const t12 = domain ?? href;
          let t13;
          if ($[48] !== t12) {
            t13 = wrapLongUnbrokenSegments(t12);
            $[48] = t12;
            $[49] = t13;
          } else {
            t13 = $[49];
          }
          let t14;
          if ($[50] !== t13) {
            t14 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-flex-col tw-gap-1 tw-p-4">{t11}<span className="tw-[overflow-wrap:anywhere] tw-break-words tw-text-sm tw-font-semibold tw-leading-snug tw-text-iron-100 tw-transition tw-duration-200 hover:tw-text-white">{t13}</span></div>;
            $[50] = t13;
            $[51] = t14;
          } else {
            t14 = $[51];
          }
          let t15;
          if ($[52] !== effectiveHref || $[53] !== linkRel || $[54] !== linkTarget || $[55] !== t14) {
            t15 = <Link href={effectiveHref} target={linkTarget} rel={linkRel} onClick={_temp2} className="tw-relative tw-flex tw-h-full tw-w-full tw-items-end tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-black/30 tw-no-underline" data-testid="og-preview-unavailable">{t10}{t14}</Link>;
            $[52] = effectiveHref;
            $[53] = linkRel;
            $[54] = linkTarget;
            $[55] = t14;
            $[56] = t15;
          } else {
            t15 = $[56];
          }
          let t16;
          if ($[57] !== hideActions || $[58] !== href || $[59] !== resolvedVariant || $[60] !== t15) {
            t16 = <LinkPreviewCardLayout href={href} variant={resolvedVariant} hideActions={hideActions}>{t15}</LinkPreviewCardLayout>;
            $[57] = hideActions;
            $[58] = href;
            $[59] = resolvedVariant;
            $[60] = t15;
            $[61] = t16;
          } else {
            t16 = $[61];
          }
          t6 = t16;
          break bb0;
        }
        let t10;
        if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-iron-400">Link unavailable</p>;
          $[62] = t10;
        } else {
          t10 = $[62];
        }
        const t11 = domain ?? href;
        let t12;
        if ($[63] !== t11) {
          t12 = wrapLongUnbrokenSegments(t11);
          $[63] = t11;
          $[64] = t12;
        } else {
          t12 = $[64];
        }
        let t13;
        if ($[65] !== effectiveHref || $[66] !== linkRel || $[67] !== linkTarget || $[68] !== t12) {
          t13 = <div className="tw-flex tw-h-full tw-min-h-0 tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-6" data-testid="og-preview-unavailable"><div className="tw-max-h-full tw-space-y-2 tw-overflow-y-auto tw-text-center">{t10}<Link href={effectiveHref} target={linkTarget} rel={linkRel} className="tw-[overflow-wrap:anywhere] tw-line-clamp-2 tw-block tw-break-words tw-text-sm tw-font-semibold tw-text-iron-100 tw-no-underline tw-transition tw-duration-200 hover:tw-text-white">{t12}</Link></div></div>;
          $[65] = effectiveHref;
          $[66] = linkRel;
          $[67] = linkTarget;
          $[68] = t12;
          $[69] = t13;
        } else {
          t13 = $[69];
        }
        let t14;
        if ($[70] !== hideActions || $[71] !== href || $[72] !== resolvedVariant || $[73] !== t13) {
          t14 = <LinkPreviewCardLayout href={href} variant={resolvedVariant} hideActions={hideActions}>{t13}</LinkPreviewCardLayout>;
          $[70] = hideActions;
          $[71] = href;
          $[72] = resolvedVariant;
          $[73] = t13;
          $[74] = t14;
        } else {
          t14 = $[74];
        }
        t6 = t14;
        break bb0;
      }
      t5 = <LinkPreviewCardLayout href={href} variant={resolvedVariant} hideActions={hideActions}>{resolvedVariant === "home" ? <Link href={effectiveHref} target={linkTarget} rel={linkRel} onClick={_temp3} className="tw-relative tw-block tw-h-full tw-min-h-0 tw-w-full tw-overflow-hidden tw-rounded-t-xl tw-border tw-border-solid tw-border-white/10 tw-bg-black/30 tw-no-underline" data-testid="og-preview-card"><GithubPreviewStatusBadge href={href} initialPreview={githubPreview} compact={true} />{imageUrl && <Image src={imageUrl} alt={title ?? domain ?? "Link preview"} fill={true} className="tw-object-cover" loading="lazy" sizes="(max-width: 768px) 100vw, 480px" unoptimized={true} />}<div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black tw-via-black/60 tw-to-black/5" /><div className="tw-relative tw-z-10 tw-flex tw-h-full tw-flex-col tw-justify-end tw-gap-2 tw-p-4">{domain && <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-200/80">{wrapLongUnbrokenSegments(domain)}</span>}<span className="tw-[overflow-wrap:anywhere] tw-break-words tw-text-base tw-font-semibold tw-leading-snug tw-text-iron-50 tw-transition tw-duration-200 hover:tw-text-white"><span className="tw-line-clamp-1">{wrapLongUnbrokenSegments(title ?? domain ?? href)}</span></span>{description && <p className="tw-[overflow-wrap:anywhere] tw-m-0 tw-line-clamp-1 tw-whitespace-pre-line tw-break-words tw-text-xs tw-text-iron-100/80">{wrapLongUnbrokenSegments(description)}</p>}</div></Link> : <div className="tw-relative tw-h-full tw-min-h-0 tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4" data-testid="og-preview-card"><GithubPreviewStatusBadge href={href} initialPreview={githubPreview} /><div className={imageUrl ? "tw-flex tw-h-full tw-min-h-0 tw-flex-row tw-gap-3" : "tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-gap-3 md:tw-flex-row"}>{imageUrl && <Link href={effectiveHref} target={linkTarget} rel={linkRel} className="tw-block tw-h-full tw-w-28 tw-flex-shrink-0 sm:tw-w-32 md:tw-w-44"><div className="tw-h-full tw-w-full tw-overflow-hidden tw-rounded-lg tw-bg-iron-900/60"><Image src={imageUrl} alt={title ?? domain ?? "Link preview"} width={1200} height={630} className="tw-h-full tw-w-full tw-object-cover" loading="lazy" sizes="(max-width: 640px) 7rem, (max-width: 768px) 8rem, 176px" unoptimized={true} /></div></Link>}<div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-justify-center tw-gap-y-1.5 tw-overflow-hidden">{domain && <span className="tw-line-clamp-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-iron-400">{wrapLongUnbrokenSegments(domain)}</span>}<Link href={effectiveHref} target={linkTarget} rel={linkRel} className="tw-[overflow-wrap:anywhere] tw-line-clamp-2 tw-block tw-break-words tw-text-base tw-font-semibold tw-leading-snug tw-text-iron-100 tw-no-underline tw-transition tw-duration-200 hover:tw-text-white">{wrapLongUnbrokenSegments(title ?? domain ?? href)}</Link>{description && <p className="tw-[overflow-wrap:anywhere] tw-m-0 tw-line-clamp-2 tw-whitespace-pre-line tw-break-words tw-text-xs tw-text-iron-300">{wrapLongUnbrokenSegments(description)}</p>}</div></div></div>}</LinkPreviewCardLayout>;
    }
    $[15] = effectiveHref;
    $[16] = hideActions;
    $[17] = href;
    $[18] = imageOnly;
    $[19] = linkRel;
    $[20] = linkTarget;
    $[21] = preview;
    $[22] = resolvedVariant;
    $[23] = title;
    $[24] = t5;
    $[25] = t6;
  } else {
    t5 = $[24];
    t6 = $[25];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  return t5;
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp(e) {
  return e.stopPropagation();
}